import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, ArkUnitOfWork, AuthService, DocumentService, UtilityService } from 'app/core/core.module';
import { Committee, CommitteeAttachment, CommitteeMember } from 'app/core/model/entity-model';
import { DxDataGridComponent, DxHtmlEditorComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import { environment } from 'environments/environment';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AttachmentDialogComponent } from '../attachment-dialog/attachment-dialog.component';
import { MinutesEmailDialogComponent } from '../minutes-email-dialog/minutes-email-dialog.component';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import moment from 'moment';

@Component({
    selector: 'app-agenda-overview',
    templateUrl: './agenda-overview.component.html',
    styleUrls: ['./agenda-overview.component.scss'],
    standalone: false,
})
export class AgendaOverviewComponent implements OnInit {
    documents: CommitteeAttachment[] = [];
    reviewOnly = true;
    committees: any;
    committeeId: number;
    executiveMembers: any;
    processing = false;
    serviceName = environment.serviceName;
    uploadHeaders: any;
    mode: string;
    valueContent: any;
    agenda: any;
    selectedAgenda: CommitteeAttachment;
    items: any[] = [];
    disableSelection: boolean = false;
    canEmail: boolean = false;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('editor') editor: DxHtmlEditorComponent;

    constructor(
        private router: Router,
        private uow: ArkUnitOfWork,
        private auth: AuthService,
        private modalService: BsModalService,
        public appInfo: AppInformationService,
        private utility: UtilityService,
        private docService: DocumentService
    ) {}

    async ngOnInit() {
        this.mode = this.router.url.indexOf('executive') > 0 ? 'Executive Committee' : 'Finance';
        this.committees = this.uow.lookups.committees;
        const committeeId = _.find(this.committees, { name: this.mode });
        this.committeeId = !!committeeId ? committeeId.id : null;
        this.executiveMembers = (await this.uow.getMembersByCommitteeIds([Committee.Committees.ExecutiveCommittee])) as CommitteeMember[];
        await this.getData();
        this.uploadHeaders = { Authorization: 'Bearer ' + this.auth.currentUser.jwtToken };
        this.reviewOnly = this.mode === 'Executive Committee' && !this.auth.hasRole('Executive:Clerk');

        this.items = [
            {
                formatName: 'header',
                formatValues: [1, 2, 3, false],
                options: { width: 250 },
            },
            {
                formatName: 'size',
                formatValues: ['11px', '14px', '16px', '20px', '36px', '72px'],
            },
            { formatName: 'separator' },
            { formatName: 'background' },
            { formatName: 'color' },
            { formatName: 'separator' },
            { formatName: 'bold' },
            { formatName: 'italic' },
            { formatName: 'underline' },
            { formatName: 'strike' },
            { formatName: 'subscript' },
            { formatName: 'superscript' },
            { formatName: 'separator' },
            { formatName: 'alignLeft' },
            { formatName: 'alignCenter' },
            { formatName: 'alignRight' },
            { formatName: 'alignJustify' },
            { formatName: 'separator' },
            { formatName: 'increaseIndent' },
            { formatName: 'decreaseIndent' },
            { formatName: 'orderedList' },
            { formatName: 'bulletList' },
            { formatName: 'insertTable' },
        ];
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    async getData() {
        this.appInfo.pleaseWaitShow();
        this.documents = (await this.uow.getCommitteeAttachments(this.committeeId, 'committee', 2)) as any[];
        this.appInfo.pleaseWaitHide();
    }

    async new(copy?: boolean) {
        const meetingDate = await this.getInput(`Meeting Date`, 'date', null, new Date());
        if (!meetingDate) return;

        const year = meetingDate.getFullYear();
        const month = meetingDate.getMonth();
        const formattedDate = moment(meetingDate).format('YYYY-MM-DD');
        const selectedRowName: string = this.dataGrid.instance.getSelectedRowsData()[0].name;
        const trimmedName = selectedRowName.substring(11);
        console.log(trimmedName);
        const fileName = `${formattedDate} ${trimmedName}`;

        const name = await this.getInput(`Enter Name`, 'text', null, `${fileName}`);
        if (!name) return;
        const agenda = this.uow.committeeAttachments.createEntity({
            committeeId: Committee.Committees.ExecutiveCommittee,
            name: name,
            filename: null,
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
            typeId: CommitteeAttachment.Types.Minutes,
            agendaText: copy ? this.selectedAgenda.agendaText : null,
        }) as CommitteeAttachment;

        await this.uow.commit();
        await this.getData();
        this.selectedAgenda = agenda;
    }

    async editCommitteeAttachment() {
        if (!this.selectedAgenda) return;
        const name = await this.getInput(`Enter Name`, 'text', null, this.selectedAgenda.name);
        if (!name) return;
        this.selectedAgenda.name = name;
        await this.uow.commit();
        await this.getData();
    }

    async save() {
        this.appInfo.pleaseWaitShow();
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
    }
    cancel() {
        this.uow.rollback();
    }

    async onSelectionChanged(e) {
        if (this.hasChanges() && !this.disableSelection) {
            this.disableSelection = true;
            const result = await confirm(`There are pending changes. Continue?  Yes to lose change, No to go back and save`, `${this.appInfo.settings.church}`);
            if (result) {
                this.uow.rollback();
            }
            if (!result) {
                this.dataGrid.instance.selectRows(e.currentDeselectedRowKeys, false);
                this.disableSelection = false;
                return;
            }
        }
        this.selectedAgenda = this.dataGrid.instance.getSelectedRowsData()[0];
        this.canEmail = this.selectedAgenda && this.selectedAgenda.agendaText !== null;
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift({
            template: 'toolbarText',
            location: 'before',
        });
    }

    rowDoubleClick(e) {
        if (!!this.reviewOnly) return;
        this.openAttachment(e.data);
    }

    attachmentDoubleClick(e) {
        this.openAttachment(e.data);
    }

    openAttachment(attachment: CommitteeAttachment) {
        if (!attachment) return;
        window.open(attachment.filename, '_blank');
    }

    rowPrepare(e) {
        if (e.rowType === 'data') {
            if (!e.data.approved && !e.data.declined) {
                e.rowElement.className = e.rowElement.className + ' waiting-for-vote';
            }
        }
    }

    async add(selectedRow?: any) {
        const modalRef = this.modalService.show(AttachmentDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                // proposal: !!selectedRow ? selectedRow : null,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                await this.getData();
            }
            modalRef.hide();
        });
    }

    submit() {}

    async email() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const attachmentIds = selectedRows.map((x) => {
            return x.committeeAttachmentId;
        });
        const email = this.uow.committeeAttachmentEmails.createEntity({
            committeeId: this.committeeId,
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
        });

        let attachmentArray = [];
        selectedRows.forEach((row) => {
            const attachmentLink = this.uow.committeeAttachmentEmailLinks.createEntity({
                emailId: email.id,
                attachmentId: row.id,
            });
            attachmentArray.push(row.name);
        });

        const modalRef = this.modalService.show(MinutesEmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                attachmentArray: attachmentArray,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (!!result && result.value) {
                email.committeeId = result.value;
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.utility.emailMinutes(email.id, result.truncatedList ? result.memberIds : null, true);
                this.appInfo.pleaseWaitHide();
                const status = alert(`${result.sendAsText ? 'Text' : 'Email'} Sent!`, `${this.appInfo.settings.church.toUpperCase()}`);
            }
            modalRef.hide();
            this.processing = false;
            await this.getData();
        });
    }

    async delete() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const result = await confirm(`Are you sure you want to delete?`, `GRRAFSCHAP`);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const ids = selectedRows.map((x) => {
            return x.id;
        });
        await this.docService.deleteDocuments(ids);
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }
    async uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    async uploaded(e) {
        if (e && e.request && e.request.responseText) {
            const fileResponses = JSON.parse(e.request.responseText);
            for (let idx = 0; idx < fileResponses.length; idx++) {
                const fileResponse = fileResponses[idx];
                // const attachment = this.uow.attachments.createEntity({
                //     bidId: this.bid.id,
                //     name: fileResponse.name,
                //     filename: fileResponse.filename,
                //     created: new Date(),
                //     createdBy: this.auth.currentUser.userName,
                // }) as Attachment;
                // this.bidAttachments.push(attachment);
            }
        }
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async uploadError(e) {
        await alert('An error occurred while attempting to upload the file(s)', `${this.appInfo.settings.church}`);
        this.appInfo.pleaseWaitHide();
    }

    async getInput(prompt: string, mode: string, array?: any[], value?: any): Promise<any> {
        let modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Graafschap Church',
                dialogPrompt: prompt,
                dialogValueArray: array,
                dialogValue: value,
                mode: mode,
            },
        });
        const promise = new Promise<any>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.success) {
                    resolve(result.value);
                }
                modalRef.hide();
                resolve(null);
            });
        });
        return promise;
    }
}
